import {zodResolver} from '@hookform/resolvers/zod';
import {MarketOptIn, UserRegisterDto} from 'puffco-api-axios-client';
import React from 'react';
import {useForm} from 'react-hook-form';
import {View} from 'react-native';
import {useDispatch} from 'react-redux';
import {z} from 'zod';

import {Button, LoadingButton} from '../../components/Button';
import {ErrorMessage} from '../../components/Form/ErrorMessage';
import {Input} from '../../components/Form/Input';
import {InputContainer} from '../../components/Form/InputContainer';
import {PasswordInput} from '../../components/Form/PasswordInput';
import {ValidatedField} from '../../components/Form/ValidatedField';
import {inputIconProps} from '../../components/Form/util';
import {Separator} from '../../components/Separator';
import {StyledCheckBox} from '../../components/StyledCheckBox';
import {Text} from '../../components/Typography';
import {Constants} from '../../constants';
import {ErrorMessages, Messages, Strings} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import {MailIcon} from '../../icons';
import {marketOptInAccess} from '../../lib/api';
import {userApi} from '../../lib/api/apis';
import {useBackPress} from '../../lib/hooks/useBackPress';
import {useBreakpoints} from '../../lib/hooks/useBreakpoints';
import {useLogin} from '../../lib/hooks/useLogin';
import {updateAppFlags} from '../../lib/redux/slices/appFlagsSlice';
import {responsive} from '../../lib/utils';
import NavigationService from '../../lib/utils/NavigationService';
import type {RootStackScreenProps} from '../../navigators/RootStackNavigator';
import {WithOptionalRedirect} from '../../navigators/params';
import {optionsToRoute, toHome} from '../../navigators/util';
import {analytics} from '../../services/analytics';
import {centeredRowStyle, underlineText} from '../../styles';
import {LogoHeader, LogoTitle} from '../components';
import {KeyboardAvoidingLayout} from '../components/composable';
import {SocialAuth} from './components/SocialAuth';
import {authStyles} from './styles';

type RegisterValues = z.infer<typeof schema>;

const schema = z.object({
  email: z
    .string()
    .trim()
    .nonempty(ErrorMessages.REQUIRED_EMAIL)
    .email(ErrorMessages.INVALID_EMAIL)
    .transform(val => val.trim())
    .superRefine(async (value, ctx) => {
      if (!value) return;

      const {data} = await userApi
        .validateFields({
          userValidateFieldsDto: {email: value},
        })
        .catch(() => ({data: undefined}));

      if (!data) return true;

      if (data.email === 'ok') return true;

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          data.email === 'taken'
            ? ErrorMessages.EMAIL_ALREADY_USED
            : ErrorMessages.INVALID_EMAIL,
      });
    }),
  password: z
    .string()
    .nonempty(ErrorMessages.REQUIRED_PASSWORD)
    .min(8, ErrorMessages.PASSWORD_MINIMUM_ERROR)
    .regex(Constants.VALID_PASSWORD_REGEX, ErrorMessages.PASSWORD_FORMAT_ERROR),
  marketOptIn: z.boolean().default(false),
});

export interface AuthProps extends WithOptionalRedirect {
  appleCode?: string;
  googleCode?: string;
  email?: string;
  password?: string;
  canSkip?: boolean;
}

interface ScreenProps extends RootStackScreenProps<typeof Screens.Register> {}

const create = async (userRegisterDto: UserRegisterDto) =>
  userApi
    .register({userRegisterDto})
    .then(r => r.data)
    .catch(error => {
      if (error?.name === 'Bad Request') throw new Error(error.message);

      throw new Error(ErrorMessages.NETWORK_ERROR);
    });

export const RegisterScreen: React.FC<ScreenProps> = ({navigation, route}) => {
  const login = useLogin();

  const dispatch = useDispatch();

  const {email, password, redirect, canSkip} = route.params ?? {};

  const defaultValues: RegisterValues = {
    email: email ?? '',
    password: password ?? '',
    marketOptIn: false,
  };

  const {isMedium} = useBreakpoints();

  const {
    control,
    handleSubmit,
    getValues,
    setError,
    formState: {isSubmitting, errors},
  } = useForm<RegisterValues>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onBlur',
  });

  useBackPress(() => {
    navigation.goBack();
    return true;
  });

  const onSubmit = React.useCallback(
    async ({email, password, marketOptIn}: RegisterValues) => {
      await create({
        password,
        email,
        ...(marketOptIn && {marketOptIn: MarketOptIn.Email}),
      });

      analytics.trackEvent('sign up', {method: 'credentials'});

      if (marketOptIn)
        await marketOptInAccess
          .subscribeToEmail({email})
          .then(() => analytics.trackEvent('optin', {content_type: 'email'}));

      dispatch(updateAppFlags({hasSeenNewUsername: false}));

      await login({email, password});

      NavigationService.instance()?.resetRoot({
        index: 0,
        routes: [optionsToRoute(redirect ?? toHome)],
      });
    },
    [dispatch, login, redirect],
  );

  return (
    <KeyboardAvoidingLayout>
      <View style={[authStyles.innerContainer, authStyles.formContainer]}>
        <LogoHeader>
          <LogoTitle>{Strings.CREATE_ACCOUNT}</LogoTitle>
        </LogoHeader>

        <View style={authStyles.bodyContainer}>
          <ValidatedField {...{control}} name="email">
            {props => (
              <InputContainer invalid={!!errors.email}>
                <MailIcon {...inputIconProps} />
                <Input
                  keyboardType="email-address"
                  placeholder={Strings.EMAIL_ADDRESS}
                  {...props}
                />
              </InputContainer>
            )}
          </ValidatedField>

          <ValidatedField {...{control}} name="password">
            {props => (
              <PasswordInput
                invalid={!!errors.password}
                placeholder="Password"
                {...props}
              />
            )}
          </ValidatedField>

          <ValidatedField
            {...{control}}
            name="marketOptIn"
            style={authStyles.optInField}>
            {props => (
              <StyledCheckBox {...props} style={authStyles.checkbox}>
                <Text variant="xs">{Messages.MARKET_OPT_IN_PROMP}</Text>
              </StyledCheckBox>
            )}
          </ValidatedField>

          {errors.root?.message && (
            <ErrorMessage>{errors.root?.message}</ErrorMessage>
          )}

          <LoadingButton
            loading={isSubmitting}
            disabled={isSubmitting}
            onPress={handleSubmit(values =>
              onSubmit(values).catch(e => {
                setError('root', {message: e.message});
              }),
            )}>
            {Strings.CREATE_ACCOUNT}
          </LoadingButton>

          <View style={centeredRowStyle}>
            <Text variant="s">{Messages.ALREADY_HAVE_AN_ACCOUNT} </Text>

            <Button
              variant="transparent"
              onPress={() => {
                navigation.replace(Screens.Login, {
                  redirect,
                  email: getValues('email'),
                  password: getValues('password'),
                  canSkip,
                });
              }}>
              <Text style={underlineText} variant="s">
                {Strings.SIGN_IN}
              </Text>
            </Button>
          </View>

          <Separator
            style={{marginTop: isMedium ? 0 : responsive(24)}}
            text={Messages.OR_CONTINUE_WITH}
          />

          <SocialAuth screen="register" />
        </View>
      </View>
    </KeyboardAvoidingLayout>
  );
};
